import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  // Zoom media/thumbnails are cloned from the visible gallery on first use so
  // large PDPs do not ship a third full-size media tree in the initial HTML.
  requiredRefs = ['dialog', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {boolean} */
  #mediaHydrated = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Gallery media nodes to clone into the zoom dialog (prefer grid, else slideshow).
   * Deferred grid items keep media inside <template> until revealed — materialize
   * them here so zoom has a complete set without loading images on initial PDP paint.
   * @returns {HTMLElement[]}
   */
  #gallerySources() {
    const gallery = this.closest('media-gallery');
    if (!gallery) return [];

    gallery.querySelectorAll('.media-gallery__grid > li > template[data-rsw-gallery-deferred]').forEach((template) => {
      if (!(template instanceof HTMLTemplateElement)) return;
      const host = template.parentElement;
      if (!(host instanceof HTMLElement)) return;
      host.append(template.content.cloneNode(true));
      template.remove();
    });

    const gridItems = /** @type {HTMLElement[]} */ ([
      ...gallery.querySelectorAll('.media-gallery__grid > li'),
    ]);
    if (gridItems.length) return gridItems;

    return /** @type {HTMLElement[]} */ ([...gallery.querySelectorAll('slideshow-slide')]);
  }

  /**
   * Build zoom media + thumbnails once from the on-page gallery.
   * Safe to call repeatedly.
   */
  ensureMediaHydrated() {
    if (this.#mediaHydrated) return;

    const list = this.querySelector('ul.dialog-zoomed-gallery');
    const { thumbnails } = this.refs;
    const sources = this.#gallerySources();

    // Do not lock hydration if the gallery is not ready yet (e.g. delayed morph).
    if (!(list instanceof HTMLElement) || !thumbnails || !sources.length) {
      return;
    }

    const fragment = document.createDocumentFragment();
    const thumbFragment = document.createDocumentFragment();

    sources.forEach((source, index) => {
      const mediaType = [...source.classList]
        .find((c) => c.startsWith('product-media-container--') && c !== 'product-media-container--zoomable')
        ?.replace('product-media-container--', '') || 'image';
      const isImage = mediaType === 'image';
      const previewRatio = source.style.getPropertyValue('--media-preview-ratio');
      const mediaId = source.querySelector('[data-media-id]')?.getAttribute('data-media-id') || String(index);

      const li = document.createElement('li');
      li.id = `product-${mediaId}-${index + 1}`;
      li.className = [
        'product-media-container',
        `product-media-container--${mediaType}`,
        isImage ? 'product-media-container--zoomable' : '',
      ]
        .filter(Boolean)
        .join(' ');
      if (previewRatio) li.style.setProperty('--media-preview-ratio', previewRatio);
      li.setAttribute('ref', 'media[]');
      if (isImage) li.setAttribute('on:click', '/close');

      const productMedia = source.querySelector('.product-media');
      if (productMedia) {
        const clone = /** @type {HTMLElement} */ (productMedia.cloneNode(true));
        const img = clone.querySelector('img.product-media__image');
        if (img instanceof HTMLImageElement) {
          img.setAttribute('sizes', '100vw');
          img.setAttribute('loading', 'lazy');
          img.removeAttribute('fetchpriority');
          img.setAttribute('ref', 'image');
        }

        if (isImage) {
          const wrap = document.createElement('drag-zoom-wrapper');
          wrap.className = 'product-media__drag-zoom-wrapper';
          wrap.appendChild(clone);
          li.appendChild(wrap);
        } else {
          li.appendChild(clone);
        }
      }

      fragment.appendChild(li);

      if (sources.length > 1) {
        const thumbImg = source.querySelector('img');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'button button-unstyled dialog-thumbnails-list__thumbnail';
        button.setAttribute('aria-label', thumbImg?.alt || `Media ${index + 1}`);
        button.setAttribute('on:click', `/handleThumbnailClick/${index}`);
        button.setAttribute('on:pointerenter', `/handleThumbnailPointerEnter/${index}`);
        if (previewRatio) {
          button.style.setProperty('--aspect-ratio', previewRatio);
          button.style.setProperty('--gallery-aspect-ratio', previewRatio);
        }
        if (index === 0) button.setAttribute('aria-selected', 'true');

        if (thumbImg instanceof HTMLImageElement) {
          const thumbClone = /** @type {HTMLImageElement} */ (thumbImg.cloneNode(true));
          thumbClone.loading = 'lazy';
          thumbClone.sizes = 'auto, 110, (min-width: 750px) 160';
          thumbClone.removeAttribute('fetchpriority');
          thumbClone.removeAttribute('ref');
          button.appendChild(thumbClone);
        }

        thumbFragment.appendChild(button);
      }
    });

    list.appendChild(fragment);
    thumbnails.appendChild(thumbFragment);

    this.#mediaHydrated = true;
    this.updatedCallback();
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    this.ensureMediaHydrated();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media?.[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;
    if (!media?.length) return;

    const mostVisibleElement = await getMostVisibleElement(media);
    if (!mostVisibleElement) return;

    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice() || !media?.length) return this.closeDialog();

    // Find the most visible image using IntersectionObserver (must not hang forever).
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = mostVisibleElement ? media.indexOf(mostVisibleElement) : -1;
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!mostVisibleElement || !slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await Promise.race([
      onAnimationEnd(this.refs.thumbnails),
      new Promise((resolve) => setTimeout(resolve, 500)),
    ]);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.classList.remove('dialog--closed');
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    this.ensureMediaHydrated();
    const { media } = this.refs;
    if (!media?.[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    this.ensureMediaHydrated();

    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media?.[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement | null>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    if (!elements?.length) {
      resolve(null);
      return;
    }

    let settled = false;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let timeoutId;
    /** @type {IntersectionObserver | undefined} */
    let observer;

    const finish = (/** @type {HTMLElement | null} */ element) => {
      if (settled) return;
      settled = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      observer?.disconnect();
      resolve(element);
    };

    // IntersectionObserver can fail to deliver a callback (empty/offscreen media).
    // Without a timeout, zoom close() awaited forever and left the modal blocking the PDP.
    timeoutId = setTimeout(() => {
      finish(elements[0] ?? null);
    }, 250);

    observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        finish(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
